/**
 * The Layout tab's Document panel — the whole of what a Document visual adds.
 *
 * A Document visual is an ordinary visual in every way that matters: the same
 * Business Area, the same criteria, the same columns dragged into the same
 * group, building the same query. What differs is only what happens to the
 * rows at the end, and there are exactly two decisions to make about that:
 *
 *  1. **Which columns identify ONE document.** This is the load-bearing one.
 *     A customer agreement run for a whole state is not one document with a
 *     state in the heading — it is one agreement per customer, and per the
 *     FINANCIAL ENTITY that signs rather than per customer code, because one
 *     party may hold several codes for several locations. Name the entity
 *     column here and the run splits by it; inside each agreement the rows are
 *     still there to list. Name nothing and the whole run is one document,
 *     which is right for a listing.
 *
 *  2. **What the document looks like**, which is not a question this screen
 *     can answer — it opens the document designer, the same one every other
 *     document form uses.
 *
 * Deliberately not a third tab and not a second designer. The columns are
 * already chosen by the time somebody gets here, and the identity is a
 * property OF that column list, so it belongs beside it.
 */
import { useEffect, useState } from 'react'
import { FileText, Loader2, Plus, X } from 'lucide-react'
import toast from 'react-hot-toast'

import { listDocumentForms, loadDocumentForm, saveDocumentForm } from '@/api/er'
import type { DocumentFormSummary } from '@/api/er'
import type { VisualModel } from './types'
import { criteriaLeaves } from './types'

/** A column the run may be identified by — placed in the layout, or a prompt. */
export interface IdentityChoice {
  name: string
  label: string
  /** `row` is an output column; `prompt` is one of the visual's criteria. */
  source: 'row' | 'prompt'
}

/**
 * Everything this visual could identify a document by.
 *
 * Both kinds are offered together because from the author's side they are one
 * question — "what makes this one document rather than another?" — and the
 * answer is sometimes a column of the result and sometimes the criterion the
 * run was made with. The document engine keeps them apart (`source`), because
 * one splits the rows and the other names the run.
 */
export function identityChoices(model: VisualModel): IdentityChoice[] {
  const out: IdentityChoice[] = []
  const seen = new Set<string>()
  const add = (raw: string, label: string, source: IdentityChoice['source']) => {
    const name = String(raw || '').trim().toUpperCase()
    if (!name || seen.has(name)) return
    seen.add(name)
    out.push({ name, label: label || name, source })
  }

  for (const group of model.LAYOUT_DATA?.visualLayout?.ColumnGroups || []) {
    for (const col of group.COLUMNS || []) {
      if (col.HIDDEN) continue
      add(String(col.NAME || col.DBNAME || ''),
          String(col.content || col.NAME || ''), 'row')
    }
  }
  for (const rule of criteriaLeaves(
    model.SQLMODEL?.CRITERIA?.query?.rules || [])) {
    if (String(rule.queryOption || '') !== 'P') continue
    add(String(rule.field || ''), String(rule.promptLabel || rule.field || ''),
        'prompt')
  }
  return out
}

/** The document form paired with this visual: same name, by the same rule. */
export function documentFormFor(
  forms: DocumentFormSummary[], objName: string,
): DocumentFormSummary | undefined {
  const wanted = String(objName || '').trim().toUpperCase()
  return forms.find((f) => String(f.anchor_obj || '').toUpperCase() === wanted)
}

export interface DocumentOutputPanelProps {
  project: string
  objName: string
  model: VisualModel
  /** Save the visual before opening the designer — it reads the model on disk. */
  onSaveFirst: () => Promise<boolean>
  onOpenDesigner: () => void
  /** Bumped by the host after the document designer closes, to re-read. */
  nonce?: number
}

export function DocumentOutputPanel({
  project, objName, model, onSaveFirst, onOpenDesigner, nonce = 0,
}: DocumentOutputPanelProps) {
  const [forms, setForms] = useState<DocumentFormSummary[] | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let alive = true
    listDocumentForms(project)
      .then((got) => { if (alive) setForms(got) })
      // A listing that fails leaves the panel saying "no document yet", which
      // is the honest reading: nothing here can be shown about one.
      .catch(() => { if (alive) setForms([]) })
    return () => { alive = false }
  }, [project, nonce])

  const paired = forms ? documentFormFor(forms, objName) : undefined
  const choices = identityChoices(model)
  // What is currently SELECTED.
  //
  // `identity` on the summary is the `row` keys only — the ones that split the
  // run. A criterion chosen as identity is stored as a `prompt` key, so
  // reading only `identity` made its chip render OFF the moment it was saved:
  // the author clicked it, it turned back on its own, forever. Both kinds are
  // keys of the document, and the chip means "this is part of what identifies
  // one" either way.
  const rowKeys = (paired?.identity || []).map((k) => String(k).toUpperCase())
  const promptKeys = (paired?.keys || [])
    .map((k) => String(k).toUpperCase())
    .filter((k) => !rowKeys.includes(k))
  const identity = [...rowKeys, ...promptKeys]
  const placed = (model.LAYOUT_DATA?.visualLayout?.ColumnGroups || [])
    .reduce((n, g) => n + (g.COLUMNS || []).length, 0)

  /**
   * Write the identity onto the paired form, creating it if there is none.
   *
   * The existing model is LOADED and patched rather than rebuilt. A save
   * replaces the whole file, so posting a form made of keys and sources alone
   * would take the body with it — somebody would set an identity and find
   * their laid-out agreement blank. Only two blocks are ever touched here.
   */
  const setIdentity = async (next: string[]) => {
    setBusy(true)
    try {
      if (!await onSaveFirst()) return
      const keys = next.map((name) => {
        const choice = choices.find((c) => c.name === name)
        return {
          key: name,
          title: choice?.label || name,
          // A criterion identifies the RUN, a column identifies a document
          // WITHIN it. Both belong in `keys[]`; `source` is what says which.
          source: choice?.source === 'prompt' ? 'prompt' : 'row',
        }
      })
      // Prompts that are NOT the identity still belong on the form, because
      // they are how the run is narrowed. Adding them here means the document
      // knows its criteria without the author restating them.
      for (const choice of choices) {
        if (choice.source !== 'prompt') continue
        if (keys.some((k) => k.key === choice.name)) continue
        keys.push({ key: choice.name, title: choice.label, source: 'prompt' })
      }

      // Load and save by the FORM's own name, not the visual's. Same-name
      // pairing is the rule, but a form that was renamed (or authored by hand)
      // would otherwise be read from a path that 404s — and the catch below
      // would then write a brand-new blank form beside it, leaving the project
      // with two documents anchored on one visual.
      const formName = paired?.obj_name || objName
      const existing = paired
        ? await loadDocumentForm(project, formName).catch(() => null)
        : null
      const docModel = (existing || {
        OBJ_NAME: objName,
        OBJ_TYPE: 'E',
        form: {
          title: `${objName.replace(/_/g, ' ')} document`,
          governance: 'none',
        },
      }) as Record<string, unknown>
      const form = (docModel.form || {}) as Record<string, unknown>

      // MERGE, never replace.
      //
      // A document may cover more than its visual — a company master for the
      // letterhead, a repository — and those are `linked` sources the author
      // added in the document designer. Replacing `transactions` wholesale
      // deleted them silently the first time somebody toggled a chip out here.
      // Only the PRIMARY is this panel's business.
      const priorTxns = Array.isArray(form.transactions)
        ? (form.transactions as Record<string, unknown>[]) : []
      const priorPrimary = priorTxns.find((t) => t?.role === 'primary') || {}
      const linked = priorTxns.filter((t) => t?.role !== 'primary')

      // Same for the keys: the document designer authors a title, a data type
      // and whether a key is mandatory, and none of that is knowable here.
      const priorKeys = Array.isArray(form.keys)
        ? (form.keys as Record<string, unknown>[]) : []
      const mergedKeys = keys.map((k) => {
        const prior = priorKeys.find(
          (p) => String(p?.key || '').toUpperCase() === k.key.toUpperCase())
        return prior ? { ...prior, ...k } : k
      })

      await saveDocumentForm(project, formName, {
        ...docModel,
        OBJ_NAME: formName,
        OBJ_TYPE: 'E',
        form: {
          ...form,
          keys: mergedKeys,
          transactions: [
            {
              ...priorPrimary,
              obj_name: objName, kind: 'visual', role: 'primary',
              on_missing: priorPrimary.on_missing || 'skip',
              key_map: Object.fromEntries(
                mergedKeys.map((k) => [k.key, k.key])),
            },
            ...linked,
          ],
        },
      })
      const got = await listDocumentForms(project)
      setForms(got)
    } catch (exc) {
      const detail = (exc as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail
      toast.error(detail || 'The document could not be updated.')
    } finally {
      setBusy(false)
    }
  }

  const open = async () => {
    setBusy(true)
    try {
      if (!await onSaveFirst()) return
      if (!paired) await setIdentity(identity)
      onOpenDesigner()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{
      marginTop: 18, padding: 14, borderRadius: 11,
      border: '1px solid var(--color-border)',
      background: 'var(--color-bg-secondary)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4,
        fontSize: 13, fontWeight: 700, color: 'var(--color-text-secondary)',
        textTransform: 'uppercase', letterSpacing: 0.4,
      }}>
        <FileText size={13} /> Document
      </div>

      <div style={{ fontSize: 13.5, color: 'var(--color-text-secondary)',
        marginBottom: 12, lineHeight: 1.6 }}>
        {paired
          ? <>Laid out in <strong>{paired.obj_name}</strong> · {placed} column
              {placed === 1 ? '' : 's'} available
              {identity.length
                ? <> · one document per <strong>{identity.join(' + ')}</strong></>
                : <> · <strong>one document for the whole run</strong></>}
            </>
          : <>No document designed yet. Choose what identifies one, then design
              it.</>}
      </div>

      {/* ── What identifies one document ── */}
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6,
        color: 'var(--color-text-secondary)' }}>
        One document per
      </div>
      {choices.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>
          Add columns to the layout first — a document is identified by one of
          them.
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6,
          marginBottom: 10 }}>
          {choices.map((c) => {
            const on = identity.includes(c.name)
            return (
              <button
                key={c.name}
                type="button"
                disabled={busy}
                onClick={() => setIdentity(on
                  ? identity.filter((k) => k !== c.name)
                  : [...identity, c.name])}
                title={c.source === 'prompt'
                  ? 'A criterion of the run — every document of one run shares it'
                  : 'A column of the result — the rows are split by it'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 9px', borderRadius: 999, fontSize: 13,
                  cursor: busy ? 'default' : 'pointer',
                  border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-border)'}`,
                  background: on ? 'var(--color-accent)' : 'transparent',
                  color: on ? '#fff' : 'var(--color-text)',
                }}
              >
                {on ? <X size={11} /> : <Plus size={11} />}
                {c.label}
                {c.source === 'prompt' && (
                  <span style={{ opacity: 0.7, fontSize: 13 }}>criterion</span>
                )}
              </button>
            )
          })}
        </div>
      )}
      {identity.length === 0 && choices.length > 0 && (
        <div style={{ fontSize: 13, color: 'var(--color-text-faint)',
          marginBottom: 10, lineHeight: 1.6 }}>
          With none chosen, every run makes a single document covering all the
          rows. Pick the column that identifies one — a customer, an entity, an
          order — to get one document each.
        </div>
      )}

      <button
        type="button"
        className="btn btn-secondary btn-sm"
        disabled={busy}
        onClick={open}
      >
        {busy ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />}
        {paired ? ' Design document' : ' Design document…'}
      </button>
    </div>
  )
}

export default DocumentOutputPanel
